import React from 'react';

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-brand-white mb-2 text-center">
          Privacy Policy
        </h1>
        <p className="text-brand-gray-blue text-center mb-8">
          Effective: February 2026
        </p>

        <p className="text-brand-gray-blue text-sm leading-relaxed mb-8">
          Thank you for visiting OptionsLeveling (the "Site"), operated by Fooba Labs LLC
          ("OptionsLeveling," "we," "us," or "our"). We are committed to protecting your
          privacy. This Privacy Policy describes our practices with respect to the collection,
          use, and disclosure of your personal information when you use our Site and services.
        </p>

        <div className="bg-green-900/20 border-2 border-green-500/50 rounded-lg p-6 mb-8">
          <h2 className="text-green-400 font-bold text-lg mb-2">
            Our Commitment
          </h2>
          <p className="text-green-300 text-sm leading-relaxed">
            We do not sell your personal information to anyone. We collect only what is necessary
            to provide and improve our services. You retain ownership of your personal
            information. We process it solely to provide, maintain, and improve the services
            described in this Privacy Policy.
          </p>
        </div>

        <div className="space-y-6">
          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              1. Who We Are
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              OptionsLeveling is an options analytics and computational modeling platform
              operated by Fooba Labs LLC. Our platform provides options screening, portfolio
              tracking, watchlist management, and analytics tools for individual investors
              and traders. For questions about this Privacy Policy, please contact us at the
              email address listed at the bottom of this page.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              OptionsLeveling does not access, connect to, or store brokerage account
              credentials or trade execution data. We are not a brokerage platform and do
              not facilitate the execution of securities transactions.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              2. Personal Information We Collect
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              When you visit our Site or register for our services, we collect the following
              categories of personal information:
            </p>
            <ul className="text-brand-gray-blue text-sm leading-relaxed space-y-3 list-disc list-inside">
              <li>
                <span className="text-brand-white font-medium">Account Information:</span>{' '}
                Username, email address, and password when you create an account. If you sign
                up via Google OAuth, we receive your name and email address from Google.
              </li>
              <li>
                <span className="text-brand-white font-medium">Payment Information:</span>{' '}
                When you subscribe to a paid plan, payment is processed securely by Stripe. We
                do not store your credit card number, CVV, or full payment details on our
                servers. We may receive limited billing information (such as subscription status
                and billing period) from Stripe.
              </li>
              <li>
                <span className="text-brand-white font-medium">Usage Data:</span>{' '}
                Information about how you use the Site, including screener queries run, contracts
                saved, watchlist groups created, and feature usage counts for subscription tier
                enforcement.
              </li>
              <li>
                <span className="text-brand-white font-medium">Technical Data:</span>{' '}
                IP address, browser type, device type, operating system, and other technical
                information collected automatically when you access the Site.
              </li>
              <li>
                <span className="text-brand-white font-medium">Cookies:</span>{' '}
                We use cookies and similar technologies to maintain your session, remember your
                preferences, and improve your experience. See Section 7 for more details.
              </li>
            </ul>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              3. How We Use Your Information
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              We use the personal information we collect for the following purposes:
            </p>
            <ul className="text-brand-gray-blue text-sm leading-relaxed space-y-2 list-disc list-inside">
              <li>Create and manage your account.</li>
              <li>Provide and deliver the services you request, including options screening, watchlist tracking, and dashboard analytics.</li>
              <li>Process payments and manage subscriptions through Stripe.</li>
              <li>Enforce subscription tier usage limits.</li>
              <li>Send you service-related communications such as password resets, account notifications, and important updates.</li>
              <li>Improve, maintain, and secure our Site and services.</li>
              <li>Respond to your inquiries and provide customer support.</li>
              <li>Detect, prevent, and address fraud, security issues, and technical problems.</li>
              <li>Comply with legal obligations.</li>
            </ul>
            <p className="text-brand-gray-blue text-sm leading-relaxed mt-3 font-medium">
              We do not use your personal information to provide personalized investment
              recommendations or financial advice of any kind.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              4. Legal Basis for Processing
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              We collect and process personal information on the following bases:
            </p>
            <ul className="text-brand-gray-blue text-sm leading-relaxed space-y-2 list-disc list-inside">
              <li>
                <span className="text-brand-white font-medium">Service Delivery:</span>{' '}
                To provide the services you have requested, including account creation, options
                screening, watchlist management, and subscription processing.
              </li>
              <li>
                <span className="text-brand-white font-medium">Contractual Obligations:</span>{' '}
                To fulfill our obligations under our Terms of Use and subscription agreements.
              </li>
              <li>
                <span className="text-brand-white font-medium">Legal Compliance:</span>{' '}
                To comply with applicable laws, regulations, and legal processes.
              </li>
              <li>
                <span className="text-brand-white font-medium">Legitimate Business Interests:</span>{' '}
                To pursue legitimate interests such as maintaining security, preventing fraud,
                conducting analytics to improve our services, and communicating with you about
                your account.
              </li>
            </ul>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              5. Information We Do Not Sell
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3 font-semibold text-brand-white">
              We do not sell your personal information to anyone. Period.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              We do not sell, rent, trade, or otherwise transfer your personal information to
              third parties for their marketing purposes. We do not share personal information
              for cross-context behavioral advertising. Your data is used solely to provide
              and improve our services as described in this Privacy Policy.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              OptionsLeveling is not a data broker and does not purchase or aggregate personal
              information for resale.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              6. Information We Share
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              We share personal information only in the following limited circumstances:
            </p>
            <ul className="text-brand-gray-blue text-sm leading-relaxed space-y-3 list-disc list-inside">
              <li>
                <span className="text-brand-white font-medium">Payment Processing:</span>{' '}
                We share necessary information with Stripe to process your subscription payments
                securely.
              </li>
              <li>
                <span className="text-brand-white font-medium">Authentication Providers:</span>{' '}
                If you use Google OAuth to sign in, information is exchanged with Google as part
                of the authentication process.
              </li>
              <li>
                <span className="text-brand-white font-medium">Hosting & Infrastructure:</span>{' '}
                Our Site is hosted on Google Cloud Platform. Your data is stored on their servers
                as part of providing the service.
              </li>
              <li>
                <span className="text-brand-white font-medium">Analytics & Monitoring:</span>{' '}
                We may use third-party analytics and monitoring tools to understand site usage,
                diagnose technical issues, and improve performance. These providers may collect
                technical data such as IP address, browser type, device information, and
                interaction patterns.
              </li>
              <li>
                <span className="text-brand-white font-medium">Legal Requirements:</span>{' '}
                We may disclose your information if required by law, regulation, legal process,
                or governmental request, or to protect the rights, property, or safety of
                OptionsLeveling, our users, or others.
              </li>
              <li>
                <span className="text-brand-white font-medium">Business Transfers:</span>{' '}
                In the event of a merger, acquisition, or sale of assets, your personal
                information may be transferred as part of that transaction. We will notify you
                of any such change.
              </li>
            </ul>
            <p className="text-brand-gray-blue text-sm leading-relaxed mt-3">
              Your information may be stored and processed in the United States.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              7. Cookies and Tracking Technologies
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              We use cookies and similar technologies to:
            </p>
            <ul className="text-brand-gray-blue text-sm leading-relaxed space-y-2 list-disc list-inside">
              <li>Keep you logged in and maintain your session.</li>
              <li>Remember your preferences and settings.</li>
              <li>Understand how you use our Site so we can improve it.</li>
              <li>Cache data locally for performance (such as watchlist results).</li>
            </ul>
            <p className="text-brand-gray-blue text-sm leading-relaxed mt-3">
              We store authentication tokens and certain performance-related data in your
              browser's local storage to maintain your session and improve performance. You
              can clear this data at any time through your browser settings.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              8. Children's Privacy
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              Our Site is not intended for individuals under the age of 18. We do not knowingly
              collect personal information from children under 18. If we become aware that we
              have collected personal data from a child under 18, we will take steps to delete
              that information promptly.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              If you believe that a child has provided us with personal information, please
              contact us immediately at the email address listed below so we can take
              appropriate steps to delete the information.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              9. Data Security
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              We implement appropriate technical and organizational security measures to protect
              your personal information, including:
            </p>
            <ul className="text-brand-gray-blue text-sm leading-relaxed space-y-2 list-disc list-inside">
              <li>Token-based authentication for API access.</li>
              <li>Secure password hashing (passwords are never stored in plain text).</li>
              <li>HTTPS encryption for all data in transit.</li>
              <li>Payment processing handled entirely by Stripe (PCI-compliant).</li>
            </ul>
            <p className="text-brand-gray-blue text-sm leading-relaxed mt-3 mb-3">
              While we strive to protect your personal information, no method of transmission
              over the Internet or method of electronic storage is 100% secure. We cannot
              guarantee absolute security.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              In the event of a data breach affecting your personal information, we will notify
              you as required by applicable law.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              10. Your Privacy Rights
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              Depending on your location, you may have the following rights regarding your
              personal information:
            </p>
            <ul className="text-brand-gray-blue text-sm leading-relaxed space-y-2 list-disc list-inside">
              <li><span className="text-brand-white font-medium">Right to Access:</span> Request a copy of the personal information we hold about you.</li>
              <li><span className="text-brand-white font-medium">Right to Correction:</span> Request correction of inaccurate personal information.</li>
              <li><span className="text-brand-white font-medium">Right to Deletion:</span> Request deletion of your personal information and account.</li>
              <li><span className="text-brand-white font-medium">Right to Opt-Out:</span> We do not sell your data, so no opt-out is necessary. You may opt out of marketing communications at any time.</li>
              <li><span className="text-brand-white font-medium">Right to Non-Discrimination:</span> We will not discriminate against you for exercising your privacy rights.</li>
            </ul>
            <p className="text-brand-gray-blue text-sm leading-relaxed mt-3">
              To exercise any of these rights, please contact us using the information provided
              below. We will respond to your request within the timeframe required by applicable
              law.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              11. Data Retention
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              We retain your personal information for as long as your account is active or as
              needed to provide you with our services. If you delete your account, we will delete
              your personal information, saved contracts, screener parameters, watchlist groups,
              and other user data.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              We may retain limited billing and transaction records as required by tax,
              accounting, or legal obligations. Any such retained records will be stored securely
              and used solely for compliance purposes.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              12. Third-Party Services
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              Our Site may contain links to third-party websites or services. Market data
              displayed on the Site is sourced from third-party providers including EODHD and
              Polygon.io. We are not responsible for the privacy practices of these third
              parties. We encourage you to review the privacy policies of any third-party
              services you interact with.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              13. Updates to This Privacy Policy
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              We may update this Privacy Policy from time to time. When we make changes, we will
              update the "Effective" date at the top of this page. Your continued use of the Site
              after any changes constitutes your acceptance of the updated Privacy Policy. We
              encourage you to review this page periodically.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              14. Governing Law
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              This Privacy Policy shall be governed by and construed in accordance with the laws
              of the State of Florida, consistent with our Terms of Use.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              15. How to Contact Us
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              If you have any questions, comments, or concerns about this Privacy Policy, or if
              you would like to exercise your privacy rights, please contact us at:{' '}
              <a href="mailto:privacy@optionsleveling.com" className="text-brand-blue hover:text-[#1a7aef] underline transition-colors">
                privacy@optionsleveling.com
              </a>
            </p>
          </section>
        </div>

        <div className="text-center mt-8 space-y-2">
          <p className="text-brand-gray-blue text-xs">
            &copy; {new Date().getFullYear()} Fooba Labs LLC. All rights reserved.
          </p>
          <div className="flex justify-center gap-4">
            <a
              href="/terms"
              className="text-brand-blue hover:text-[#1a7aef] text-sm transition-colors"
            >
              Terms of Use
            </a>
            <a
              href="/login"
              className="text-brand-blue hover:text-[#1a7aef] text-sm transition-colors"
            >
              Back to Login
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
